import React from 'react';
import { Spinner, Button, Badge } from 'react-bootstrap';
import { DateTime } from 'luxon';
import DOMPurify from 'dompurify';
import { showUtcClock } from '../../utils/utcDateTime';
import './announcementModal.css';

const AnnouncementList = ({ announcements, isLoading, onDelete, onEdit }) => {
  // An announcement is only live when BOTH the active flag is on and the
  // current time is inside the window — matches the backend's /active filter.
  const isAnnouncementRunning = announcement => {
    if (!announcement.active) return false;

    // Get current time in UTC
    const now = DateTime.utc();

    // Parse the UTC times from the API
    const start = DateTime.fromISO(announcement.startTime, { zone: 'utc' });
    const end = DateTime.fromISO(announcement.endTime, { zone: 'utc' });

    return now >= start && now <= end;
  };

  if (isLoading && !announcements.length) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!isLoading && !announcements.length) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">No announcements found</p>
      </div>
    );
  }

  return (
    <div className="container">
      <table className="table table-striped">
        <thead>
          <tr>
            <th>Name</th>
            <th>Content</th>
            <th>Start Time (UTC)</th>
            <th>End Time (UTC)</th>
            <th>Active</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan="6" className="text-center py-4">
                <Spinner animation="border" role="status" variant="primary">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </td>
            </tr>
          ) : (
            announcements.map(announcement => {
              const sanitizedDescription = DOMPurify.sanitize(announcement.description || '');
              const descriptionText = sanitizedDescription
                .replace(/<[^>]+>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();
              const truncatedDescription =
                descriptionText.length > 120
                  ? `${descriptionText.slice(0, 120)}...`
                  : descriptionText;
              const running = isAnnouncementRunning(announcement);
              return (
                <tr key={announcement.id}>
                  <td>{announcement.name}</td>
                  <td>
                    <div className="description-content" title={descriptionText}>
                      {truncatedDescription || '—'}
                    </div>
                  </td>
                  <td>{showUtcClock(announcement.startTime)}</td>
                  <td>{showUtcClock(announcement.endTime)}</td>
                  <td>
                    {announcement.active ? (
                      <Badge bg="success">Yes</Badge>
                    ) : (
                      <Badge bg="secondary">No</Badge>
                    )}
                  </td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      {running && (
                        <Badge bg="success" className="px-3 py-2">
                          RUNNING
                        </Badge>
                      )}
                      <Button variant="primary" size="sm" onClick={() => onEdit(announcement)}>
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => onDelete(announcement.id, running)}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AnnouncementList;
